import { useState, useEffect, useRef, useContext } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tooltip'

import UIContext from 'ui_context'
import {
  useHasGlobalAverageAllData,
  useHasGlobalAverageLandcoverSubsetData,
  useHasGlobalAveragePolygonSubsetData,
  getLccForUser,
  getPolygonCountForUser
} from '../../hooks/useHasGlobalAverageData'

import './style.scss'

const LAND_COVER_CLASS_URL =
  'https://fluxnet.org/data/badm-data-templates/igbp-classification/'

const FLUXNET_DATASET_URL = 'https://fluxnet.org/data/fluxnet2015-dataset/'

export default function Banner() {
  const { userSub, sub } = useContext(UIContext)
  const isViewingDifferentSub = userSub !== sub
  const hasGlobalAverageAllData = useHasGlobalAverageAllData()
  const hasGlobalAverageLandcoverSubsetData =
    useHasGlobalAverageLandcoverSubsetData()
  const hasGlobalAveragePolygonSubsetData =
    useHasGlobalAveragePolygonSubsetData()
  const hasTooltipData =
    hasGlobalAverageAllData ||
    hasGlobalAverageLandcoverSubsetData ||
    hasGlobalAveragePolygonSubsetData
  const [showTooltip, setShowTooltip] = useState(false)

  const { t } = useTranslation()
  const timeoutRef = useRef<NodeJS.Timeout | number | null>(null)
  const isHoveringTooltip = useRef(false)
  const timeout = 300

  useEffect(() => {
    if (showTooltip) {
      const tooltipEl = document.getElementById('banner-tooltip')
      if (tooltipEl == null) return

      const handleTooltipEnter = () => {
        isHoveringTooltip.current = true
        if (timeoutRef.current != null) {
          clearTimeout(timeoutRef.current)
        }
        setShowTooltip(true)
      }

      const handleTooltipLeave = () => {
        isHoveringTooltip.current = false

        timeoutRef.current = setTimeout(() => {
          if (!isHoveringTooltip.current) {
            setShowTooltip(false)
          }
        }, timeout)
      }

      tooltipEl.addEventListener('mouseenter', handleTooltipEnter)
      tooltipEl.addEventListener('mouseleave', handleTooltipLeave)

      return () => {
        tooltipEl.removeEventListener('mouseenter', handleTooltipEnter)
        tooltipEl.removeEventListener('mouseleave', handleTooltipLeave)
      }
    }
  }, [showTooltip])

  const handleMouseEnter = () => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current)
    }
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      if (!isHoveringTooltip.current) {
        setShowTooltip(false)
      }
    }, timeout)
  }

  const getBannerTranslationKey = () => {
    if (hasGlobalAverageAllData) {
      return 'banner_global_average_all.summary'
    }

    if (hasGlobalAverageLandcoverSubsetData) {
      return 'banner_global_average_landcover_subset.summary'
    }

    if (hasGlobalAveragePolygonSubsetData) {
      return 'banner_global_average_polygon_subset.summary'
    }

    return ''
  }

  const getBannerTanslationVariables = () => {
    let values = {}

    if (hasGlobalAverageLandcoverSubsetData) {
      values = { lccAbbreviation: getLccForUser(sub) }
    }

    if (hasGlobalAveragePolygonSubsetData) {
      const { totalPolygonCount, polygonsWithGlobalAverageCount } =
        getPolygonCountForUser(sub)

      values = { totalPolygonCount, polygonsWithGlobalAverageCount }
    }

    return values
  }

  const getTooltipTranslationKey = () => {
    if (hasGlobalAverageAllData) {
      return 'banner_global_average_all.text'
    }

    if (hasGlobalAverageLandcoverSubsetData) {
      return 'banner_global_average_landcover_subset.text'
    }

    if (hasGlobalAveragePolygonSubsetData) {
      return 'banner_global_average_polygon_subset.text'
    }

    return ''
  }

  const getTooltipTanslationVariables = () => {
    let values = {}

    if (hasGlobalAverageLandcoverSubsetData) {
      values = {
        lccAbbreviation: getLccForUser(sub),
        lccFullForm:
          t(`landcover_class.${getLccForUser(sub)?.toLowerCase()}`) ?? ''
      }
    }

    if (hasGlobalAveragePolygonSubsetData) {
      const { totalPolygonCount, polygonsWithGlobalAverageCount } =
        getPolygonCountForUser(sub)

      values = { totalPolygonCount, polygonsWithGlobalAverageCount }
    }

    return values
  }

  return (
    <div className="banner">
      {hasTooltipData && (
        <Tooltip
          anchorSelect={'.banner-more-info'}
          place="bottom"
          noArrow
          style={{ maxWidth: '600px', pointerEvents: 'auto' }}
          isOpen={showTooltip}
          className="banner-tooltip"
          id="banner-tooltip"
        >
          <Trans
            i18nKey={getTooltipTranslationKey()}
            values={getTooltipTanslationVariables()}
            components={{
              strong: <strong />,
              firstLink: (
                <a
                  href={LAND_COVER_CLASS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'underline' }}
                />
              ),
              secondLink: (
                <a
                  href={FLUXNET_DATASET_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'underline' }}
                />
              )
            }}
          />
        </Tooltip>
      )}
      <p>
        {isViewingDifferentSub && (
          <>
            <Trans i18nKey="sub_warning_bar.label_prefix" values={{ sub }}>
              Viewing data for sub {{ sub }}
            </Trans>
          </>
        )}
        {isViewingDifferentSub && hasTooltipData && <>{'.\u00A0'}</>}
        <Trans
          i18nKey={getBannerTranslationKey()}
          values={getBannerTanslationVariables()}
          components={{
            span: (
              <span
                className="banner-more-info"
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            )
          }}
        />
      </p>
    </div>
  )
}
